import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Pencil, Check, X, ShieldCheck, UserRound } from "lucide-react";

const M = [["nb_gfp", "NB GFP", true], ["pl_specialty", "PL & Spec.", false], ["life_ip", "Life I&P", false], ["bi_nb", "BI NB", false]];
const fmt = (v, money) => (v == null ? <span className="text-slate-400 italic text-xs">Not entered</span> : money ? `$${Number(v).toLocaleString()}` : Number(v).toLocaleString());
const fmtD = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }) : "");

function Source({ m }) {
  if (!m.entered) return null;
  if (m.verified) return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700" title={`Verified by ${m.verified_by_name || "staff"}`}><ShieldCheck className="w-3 h-3" />Verified</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700" title={m.entered_by_name || ""}>
    <UserRound className="w-3 h-3" />{m.source === "participant" ? "Self-reported" : "Staff entered"} · unverified</span>;
}

function Row({ m, editable, canVerify, save }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState({});
  const [ver, setVer] = useState(m.verified);
  const start = () => { setV(Object.fromEntries(M.map(([k]) => [k, m.entry?.[k] ?? ""]))); setVer(m.verified); setEdit(true); };
  const submit = async () => {
    const body = Object.fromEntries(M.map(([k]) => [k, v[k] === "" ? null : Number(v[k])]));
    if (canVerify) body.verified = ver;
    if (await save(m.month, body)) setEdit(false);
  };
  return (
    <tr className={`border-t border-slate-200 ${m.deadlines.length ? "bg-[#E8F1F8]/60" : ""}`} data-testid={`abp-month-${m.month}`}>
      <td className="px-2 py-2 text-left whitespace-nowrap"><p className="font-semibold text-slate-900">Month {m.month}</p>
        <p className="text-[11px] text-slate-500">{m.period_start ? `${fmtD(m.period_start)} – ${fmtD(m.period_end)}` : "Start date not set"}</p></td>
      <td className="px-2 py-2 text-left text-xs">{m.deadlines.length ? <span className="font-semibold text-[#003A70]">{m.deadlines.join(", ")} due</span> : <span className="text-slate-400">—</span>}</td>
      {M.map(([k, , money]) => (
        <td key={k} className="px-2 py-2 tabular-nums">{edit
          ? <Input type="number" min="0" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="h-8 w-24 text-right" data-testid={`abp-month-${m.month}-${k}`} />
          : fmt(m.entry?.[k], money)}</td>))}
      <td className="px-2 py-2 text-[11px] text-slate-600 tabular-nums whitespace-nowrap">
        {m.cumulative.nb_gfp == null ? "—" : `$${Number(m.cumulative.nb_gfp).toLocaleString()}`}
        {m.next_target && <span className="block text-slate-400">next: {m.next_target.label} ${Number(m.next_target.nb_gfp || 0).toLocaleString()}</span>}</td>
      <td className="px-2 py-2">{edit && canVerify
        ? <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!ver} onChange={(e) => setVer(e.target.checked)} data-testid={`abp-month-${m.month}-verify`} />Verified</label>
        : <Source m={m} />}</td>
      <td className="px-2 py-2 text-right whitespace-nowrap">{editable && (edit
        ? <><Button size="sm" variant="ghost" onClick={submit} data-testid={`abp-month-${m.month}-save`}><Check className="w-4 h-4 text-emerald-600" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setEdit(false)}><X className="w-4 h-4" /></Button></>
        : <Button size="sm" variant="ghost" onClick={start} data-testid={`abp-month-${m.month}-edit`}><Pencil className="w-3.5 h-3.5" /></Button>)}</td>
    </tr>
  );
}

export function MonthlyTable({ data, staff, onData }) {
  const save = async (n, body) => {
    try {
      const url = staff ? `${data.api_base}/person/${data.person.id}/months/${n}` : `${data.api_base}/me/months/${n}`;
      const { data: d } = await api.put(url, body);
      onData(d); toast.success(`Month ${n} saved`); return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };
  return (
    <section data-testid="abp-monthly">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1 mb-3">
        <h2 className="font-display text-lg font-semibold text-slate-900">Monthly results</h2>
        <p className="text-xs text-slate-500 max-w-xl">{staff ? "Enter each month's production and tick Verified once checked against the official Tracker." : "Enter or correct your own monthly production. Your numbers show as self-reported until your district team verifies them against the official Tracker."}</p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm text-right min-w-[860px]">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr>
            <th className="px-2 py-2 text-left">Program month</th><th className="px-2 py-2 text-left">Requirements</th>
            {M.map(([k, l]) => <th key={k} className="px-2 py-2">{l}</th>)}<th className="px-2 py-2">Cumulative NB GFP</th><th className="px-2 py-2 text-left">Status</th><th />
          </tr></thead>
          <tbody>{data.months.map((m) => <Row key={m.month} m={m} editable={data.can_edit_months} canVerify={staff && data.can_verify} save={save} />)}</tbody>
        </table>
      </div>
    </section>
  );
}
